import { useEffect, useMemo, useState, useId } from "react";
import { DynamicLogo } from "./DynamicLogo";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  Building2,
  FileText,
  Settings,
  Settings2,
  BarChart3,
  Wallet,
  ChevronRight,
  Menu,
  X,
  Boxes,
  Shield,
  ChevronsDownUp,
  Wrench,
  UserCheck,
  KeyRound,
  Contact,
  Target,
  CalendarDays,
  CheckSquare,
  Mail,
  Package,
  FolderTree,
  Percent,
  Tags,
  Cpu,
  Truck,
  Warehouse,
  PackageSearch,
  ClipboardList,
  ShoppingCart,
  PieChart,
  Printer,
  History,
  TrendingUp,
  Receipt,
  CreditCard,
  Gauge,
  // Car removed - using Bike for scooter icon
  ShieldCheck,
  FileStack,
  Cog,
  Lock,
  Globe,
  Bell,
  MessageSquare,
  LogOut,
  Crown,
  FileUp,
  Download,
  Sparkles,
  PanelLeft,
  Accessibility,
  Briefcase,
  UserPlus,
  GraduationCap,
  BadgeEuro,
  Palmtree,
  Bot,
  BookOpen,
  Megaphone,
  MousePointerClick,
  Copy,
  Bike,
  Calendar,
  Phone,
  Layers,
  Ticket,
  Store,
  List,
  Battery,
  Zap,
  Euro,
  MapPin,
  Map as MapIcon,
  Star,
  ArrowDownRight,
  Link2,
  Server,
  Calculator,
  Search,
  Timer,
  Clock,
  Heart,
  AlertTriangle,
  QrCode,
  Columns3,
  Reply,
  Activity,
  RotateCcw,
} from "lucide-react";
import { Send } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { usePermissions, AppModule } from "@/hooks/usePermissions";
import { useWorkspace } from "@/hooks/useWorkspace";
import { Skeleton } from "@/components/ui/skeleton";
import { useSuperAdmin } from "@/hooks/useSuperAdmin";
import { WorkspaceSelector } from "./WorkspaceSelector";
import { getHelpAccess } from "@/lib/helpAccess";

interface NavChild {
  title: string;
  href: string;
  module: AppModule;
  icon: React.ElementType;
}

interface NavItem {
  title: string;
  icon: React.ElementType;
  href?: string;
  module?: AppModule;
  children?: NavChild[];
}

const navItems: NavItem[] = [
  { title: "Dashboard", icon: LayoutDashboard, href: "/dashboard", module: "dashboard" },
  {
    title: "Usuarios",
    icon: Users,
    children: [
      { title: "Lista de Usuarios", href: "/users", module: "users", icon: UserCheck },
      { title: "Roles y Permisos", href: "/users/roles", module: "users_roles", icon: KeyRound },
    ],
  },
  {
    title: "CRM",
    icon: Building2,
    children: [
      { title: "Dashboard", href: "/crm", module: "crm_clients", icon: LayoutDashboard },
      { title: "Clientes", href: "/crm/clients", module: "crm_clients", icon: Building2 },
      { title: "Contactos", href: "/crm/contacts", module: "crm_contacts", icon: Contact },
      { title: "Oportunidades", href: "/crm/opportunities", module: "crm_opportunities", icon: Target },
      { title: "Citas", href: "/crm/appointments", module: "crm_appointments", icon: CalendarDays },
      { title: "Tareas", href: "/crm/tasks", module: "crm_clients", icon: CheckSquare },
      { title: "Email Marketing", href: "/crm/campaigns", module: "crm_clients", icon: Mail },
      { title: "Zadarma Llamadas", href: "/crm/zadarma", module: "crm_clients", icon: Phone },
    ],
  },
  {
    title: "ERP",
    icon: Boxes,
    children: [
      { title: "Dashboard", href: "/erp", module: "erp_products", icon: LayoutDashboard },
      { title: "Productos", href: "/erp/products", module: "erp_products", icon: Package },
      { title: "Categorías", href: "/erp/categories", module: "erp_products", icon: FolderTree },
      { title: "Marcas", href: "/erp/brands", module: "erp_products", icon: Tags },
      { title: "Modelos", href: "/erp/models", module: "erp_products", icon: Cpu },
      { title: "Garantías", href: "/erp/warranties", module: "erp_products", icon: ShieldCheck },
      { title: "Paquetes/Bundles", href: "/erp/bundles", module: "erp_products", icon: Layers },
      { title: "Coste de Servicio", href: "/erp/service-costs", module: "erp_products", icon: Cog },
      { title: "Etiquetas", href: "/erp/labels", module: "erp_products", icon: Tags },
      { title: "Proveedores", href: "/erp/suppliers", module: "erp_products", icon: Truck },
      { title: "Impuestos Proveedor", href: "/erp/supplier-tax-types", module: "erp_products", icon: Receipt },
      { title: "Almacenes", href: "/erp/warehouses", module: "erp_inventory", icon: Warehouse },
      { title: "Inventario", href: "/erp/inventory", module: "erp_inventory", icon: PackageSearch },
      { title: "Facturas Compra", href: "/erp/purchase-invoices", module: "erp_inventory", icon: FileUp },
      { title: "Reporte Stock", href: "/erp/stock-report", module: "erp_inventory", icon: ClipboardList },
    ],
  },
  {
    title: "Finanzas",
    icon: Wallet,
    children: [
      { title: "Dashboard", href: "/finance", module: "finance_invoices", icon: PieChart },
      { title: "Autónomo", href: "/finance/autonomo", module: "finance_invoices", icon: Calculator },
      { title: "Empresa", href: "/finance/empresa", module: "finance_invoices", icon: Building2 },
      { title: "Punto de Venta", href: "/finance/pos", module: "finance_invoices", icon: ShoppingCart },
      { title: "Historial Ventas", href: "/finance/pos/history", module: "finance_invoices", icon: History },
      { title: "Análisis Ventas", href: "/finance/pos/analytics", module: "finance_invoices", icon: TrendingUp },
      { title: "Vales Reembolso", href: "/finance/vouchers", module: "finance_invoices", icon: Ticket },
      { title: "Presupuestos", href: "/finance/quotes", module: "finance_invoices", icon: FileText },
      { title: "Facturación Externas", href: "/finance/invoices", module: "finance_invoices", icon: Receipt },
      { title: "Facturas Internas", href: "/finance/internal-invoices", module: "finance_invoices", icon: Receipt },
      { title: "Pagos", href: "/finance/payments", module: "finance_payments", icon: CreditCard },
      { title: "Gastos", href: "/finance/expenses", module: "finance_expenses" as AppModule, icon: ArrowDownRight },
      { title: "Órdenes Compra", href: "/finance/orders", module: "erp_orders" as AppModule, icon: ShoppingCart },
      { title: "Cat. Gastos", href: "/finance/expense-categories", module: "finance_expenses" as AppModule, icon: FolderTree },
      { title: "Impuestos", href: "/finance/taxes", module: "finance_expenses" as AppModule, icon: Percent },
      { title: "Impuestos por operación", href: "/finance/operation-taxes", module: "finance_expenses" as AppModule, icon: Percent },
      { title: "Cuentas bancarias", href: "/finance/bank-accounts", module: "finance_expenses" as AppModule, icon: CreditCard },
      { title: "Presupuesto mensual", href: "/finance/budget", module: "finance_expenses" as AppModule, icon: TrendingUp },
      { title: "Cobros por tienda", href: "/finance/store-collections", module: "finance_expenses" as AppModule, icon: TrendingUp },
      { title: "Gastos por proveedor", href: "/finance/suppliers", module: "finance_expenses" as AppModule, icon: Truck },
      { title: "Conciliación mensual", href: "/finance/monthly-reconciliation", module: "finance_expenses" as AppModule, icon: Link2 },
      { title: "Conciliación", href: "/finance/reconciliation", module: "finance_reconciliation" as AppModule, icon: Link2 },
      { title: "Reglas Concil.", href: "/finance/reconciliation-rules", module: "finance_reconciliation" as AppModule, icon: Sparkles },
      { title: "VeriFactu", href: "/finance/verifactu", module: "finance_invoices" as AppModule, icon: QrCode },
    ],
  },
  {
    title: "Seguros",
    icon: ShieldCheck,
    children: [
      { title: "Dashboard", href: "/insurance", module: "insurance_clients" as AppModule, icon: Gauge },
      { title: "Asegurados", href: "/insurance/clients", module: "insurance_clients" as AppModule, icon: Users },
      { title: "Tipos de Seguro", href: "/insurance/types", module: "insurance_clients" as AppModule, icon: FileText },
      { title: "Cleverea - Cotizar", href: "/insurance/cleverea/quote", module: "insurance_cleverea" as AppModule, icon: ShieldCheck },
      { title: "Cleverea - Cotizaciones", href: "/insurance/cleverea/quotes", module: "insurance_cleverea" as AppModule, icon: FileText },
      { title: "Configuración", href: "/insurance/settings", module: "insurance_clients" as AppModule, icon: Settings },
    ],
  },
  {
    title: "Taller",
    icon: Wrench,
    children: [
      { title: "Dashboard", href: "/workshop", module: "workshop_orders" as AppModule, icon: Gauge },
      { title: "Órdenes de Trabajo", href: "/workshop/orders", module: "workshop_orders" as AppModule, icon: ClipboardList },
      { title: "Vehículos", href: "/workshop/vehicles", module: "workshop_vehicles" as AppModule, icon: Bike },
      { title: "Garantías / RMA", href: "/workshop/rma", module: "workshop_orders" as AppModule, icon: ShieldCheck },
      { title: "Mantenimientos", href: "/workshop/maintenance", module: "workshop_orders" as AppModule, icon: Bike },
      { title: "Historial de Reparaciones", href: "/workshop/history", module: "workshop_orders" as AppModule, icon: FileStack },
      // Facturación removed in favor of Historial Facturas (RepairInvoiceHistoryPage)
      { title: "Historial Facturas", href: "/workshop/invoices/history", module: "workshop_invoices" as AppModule, icon: History },
      { title: "Reportes", href: "/workshop/reports", module: "workshop_orders" as AppModule, icon: BarChart3 },
    ],
  },
  {
    title: "Operaciones",
    icon: Activity,
    children: [
      { title: "Dashboard", href: "/operations", module: "erp_inventory" as AppModule, icon: LayoutDashboard },
      { title: "Movimientos Stock", href: "/operations/inventory-movements", module: "erp_inventory" as AppModule, icon: PackageSearch },
      { title: "Unidades Serializadas", href: "/operations/serial-units", module: "erp_inventory" as AppModule, icon: QrCode },
      { title: "Ubicaciones", href: "/operations/stock-locations", module: "erp_inventory" as AppModule, icon: MapPin },
      { title: "Alertas Inventario", href: "/operations/alerts", module: "erp_inventory" as AppModule, icon: Bell },
      { title: "Devoluciones (RMA)", href: "/operations/rma", module: "erp_inventory" as AppModule, icon: RotateCcw },
      { title: "Garantías", href: "/operations/warranties", module: "erp_inventory" as AppModule, icon: ShieldCheck },
      { title: "Dashboard Financiero", href: "/operations/financial", module: "erp_inventory" as AppModule, icon: TrendingUp },
      { title: "Rentabilidad", href: "/operations/product-profitability", module: "erp_inventory" as AppModule, icon: PieChart },
      { title: "Costes Proveedores", href: "/operations/supplier-costs", module: "erp_inventory" as AppModule, icon: Building2 },
      { title: "Registro Auditoría", href: "/operations/logs", module: "erp_inventory" as AppModule, icon: Shield },
    ],
  },
  {
    title: "HRM",
    icon: Users,
    children: [
      { title: "Dashboard", href: "/hrm", module: "hrm_employees" as AppModule, icon: LayoutDashboard },
      { title: "Empleados", href: "/hrm/employees", module: "hrm_employees" as AppModule, icon: UserCheck },
      { title: "Departamentos", href: "/hrm/departments", module: "hrm_employees" as AppModule, icon: Building2 },
      { title: "Puestos", href: "/hrm/positions", module: "hrm_employees" as AppModule, icon: Briefcase },
      { title: "Vacaciones", href: "/hrm/time-off", module: "hrm_employees" as AppModule, icon: Palmtree },
      { title: "Festivos", href: "/hrm/holidays", module: "hrm_employees" as AppModule, icon: CalendarDays },
      { title: "Bajas Médicas", href: "/hrm/sick-leaves", module: "hrm_employees" as AppModule, icon: Heart },
      { title: "Reclutamiento", href: "/hrm/recruitment", module: "hrm_recruitment" as AppModule, icon: UserPlus },
      { title: "Nóminas", href: "/hrm/payroll", module: "hrm_payroll" as AppModule, icon: BadgeEuro },
      { title: "Formación", href: "/hrm/training", module: "hrm_training" as AppModule, icon: GraduationCap },
      { title: "Control Horario", href: "/hrm/time-tracking", module: "hrm_time_tracking" as AppModule, icon: Timer },
      { title: "Incidencias Horarias", href: "/hrm/time-incidents", module: "hrm_time_tracking" as AppModule, icon: AlertTriangle },
      { title: "Mi Fichaje", href: "/hrm/clock", module: "hrm_time_tracking" as AppModule, icon: Clock },
      { title: "Documentos Empleado", href: "/hrm/documents", module: "hrm_employees" as AppModule, icon: FileText },
      { title: "Push Empleados", href: "/hrm/push-notifications", module: "hrm_employees" as AppModule, icon: Bell },
    ],
  },
  {
    title: "Chatbot WhatsApp",
    icon: Bot,
    children: [
      { title: "Dashboard", href: "/chatbot", module: "chatbot_settings" as AppModule, icon: LayoutDashboard },
      { title: "Conversaciones", href: "/chatbot/conversations", module: "chatbot_conversations" as AppModule, icon: MessageSquare },
      { title: "Pipeline", href: "/chatbot/pipeline", module: "chatbot_conversations" as AppModule, icon: Columns3 },
      { title: "Métricas", href: "/chatbot/metrics", module: "chatbot_conversations" as AppModule, icon: BarChart3 },
      { title: "Automatizaciones", href: "/chatbot/automations", module: "chatbot_settings" as AppModule, icon: Zap },
      { title: "Respuestas Rápidas", href: "/chatbot/quick-replies", module: "chatbot_settings" as AppModule, icon: Reply },
      { title: "Base Conocimiento", href: "/chatbot/knowledge", module: "chatbot_knowledge" as AppModule, icon: BookOpen },
      { title: "Promociones", href: "/chatbot/promotions", module: "chatbot_promotions" as AppModule, icon: Megaphone },
      { title: "Anuncios Click-WA", href: "/chatbot/ads", module: "chatbot_settings" as AppModule, icon: MousePointerClick },
      { title: "Configuración", href: "/chatbot/settings", module: "chatbot_settings" as AppModule, icon: Settings },
    ],
  },
  {
    title: "Alquiler Vehículos",
    icon: Bike,
    children: [
      { title: "Dashboard", href: "/rental", module: "vehicle_rental" as AppModule, icon: LayoutDashboard },
      { title: "Alquileres", href: "/rental/rentals", module: "vehicle_rental" as AppModule, icon: ClipboardList },
      { title: "Flota", href: "/rental/fleet", module: "vehicle_rental" as AppModule, icon: Bike },
      { title: "Categorías", href: "/rental/categories", module: "vehicle_rental" as AppModule, icon: FolderTree },
      { title: "Planes", href: "/rental/plans", module: "vehicle_rental" as AppModule, icon: Calendar },
      { title: "Accesorios", href: "/rental/accessories", module: "vehicle_rental" as AppModule, icon: Package },
      { title: "WhatsApp", href: "/rental/whatsapp-settings", module: "vehicle_rental" as AppModule, icon: Send },
      { title: "Monitoreo WhatsApp", href: "/rental/whatsapp-monitoring", module: "vehicle_rental" as AppModule, icon: Activity },
      { title: "Discrepancias", href: "/admin/rental-discrepancies", module: "vehicle_rental" as AppModule, icon: AlertTriangle },
    ],
  },
  {
    title: "Manufactura",
    icon: Battery,
    children: [
      { title: "Diseñador Pack", href: "/manufacturing/designer", module: "dashboard" as AppModule, icon: Battery },
      { title: "Historial Packs", href: "/manufacturing/designer-history", module: "dashboard" as AppModule, icon: History },
      { title: "Especificaciones", href: "/manufacturing/cell-specs", module: "dashboard" as AppModule, icon: Cpu },
      { title: "Balanceo", href: "/manufacturing/balanceo", module: "dashboard" as AppModule, icon: Zap },
      { title: "Costes", href: "/manufacturing/costes", module: "dashboard" as AppModule, icon: Euro },
    ],
  },
  { title: "Reportes", icon: BarChart3, href: "/reports", module: "reports" },
  { title: "Documentos", icon: FileText, href: "/documents", module: "documents" },
  {
    title: "Configuración",
    icon: Settings,
    children: [
      { title: "General", href: "/settings/general", module: "settings", icon: Cog },
      { title: "Seguridad", href: "/settings/security", module: "settings", icon: Lock },
      { title: "Internacionalización", href: "/settings/i18n", module: "settings", icon: Globe },
      { title: "Notificaciones", href: "/settings/notifications", module: "settings", icon: Bell },
      { title: "Comunicaciones", href: "/settings/communications", module: "settings", icon: MessageSquare },
      { title: "Plantillas Email", href: "/settings/email-templates", module: "settings", icon: Mail },
      { title: "Impresión", href: "/settings/print", module: "settings", icon: Printer },
    ],
  },
  { title: "Mi Suscripción", icon: Crown, href: "/settings/subscription", module: "dashboard" },
  {
    title: "Formación",
    icon: GraduationCap,
    children: [
      { title: "Dashboard", href: "/formation", module: "dashboard", icon: LayoutDashboard },
      { title: "Errores y soluciones", href: "/admin/help", module: "dashboard", icon: List },
      { title: "Gestión Cursos", href: "/admin/formation", module: "dashboard", icon: List },
    ],
  },
];

// Super admin only navigation items
const superAdminNavItems: NavItem[] = [
  {
    title: "Admin",
    icon: Lock,
    children: [
      { title: "Administradores", href: "/admin/administradores", module: "dashboard" as AppModule, icon: Lock },
      { title: "Tiendas", href: "/admin/tiendas", module: "dashboard" as AppModule, icon: Globe },
      { title: "Clientes", href: "/admin/clientes", module: "dashboard" as AppModule, icon: List },
      { title: "Locales", href: "/admin/locales", module: "dashboard" as AppModule, icon: Globe },
      { title: "Webs locales", href: "/admin/local-websites", module: "dashboard" as AppModule, icon: Globe },
      { title: "Alquiler", href: "/admin/alquiler", module: "dashboard" as AppModule, icon: List },
      { title: "Auditoría alquileres", href: "/admin/rentals/audit", module: "dashboard" as AppModule, icon: FileText },
      { title: "Discrepancias", href: "/admin/rental-discrepancies", module: "dashboard" as AppModule, icon: List },
      { title: "Disputas", href: "/admin/rentals/disputes", module: "dashboard" as AppModule, icon: FileText },
      { title: "Forense alquileres", href: "/admin/rental-forensic", module: "dashboard" as AppModule, icon: Lock },
      { title: "Roles forenses", href: "/admin/forensic-roles", module: "dashboard" as AppModule, icon: Lock },
      { title: "SaaS", href: "/admin/saas", module: "dashboard" as AppModule, icon: Crown },
      { title: "Suscripciones", href: "/admin/suscripciones", module: "dashboard" as AppModule, icon: Crown },
      { title: "Sistema", href: "/admin/system", module: "dashboard" as AppModule, icon: Settings },
      { title: "Auditoría seguridad", href: "/admin/security-audit", module: "dashboard" as AppModule, icon: Lock },
      { title: "Logs de seguridad", href: "/admin/security-audit-logs", module: "dashboard" as AppModule, icon: FileText },
      { title: "SLA auditoría", href: "/admin/security-audit/sla-logs", module: "dashboard" as AppModule, icon: FileText },
      { title: "Verificar recibos", href: "/admin/security-audit/verify", module: "dashboard" as AppModule, icon: Lock },
      { title: "Test HMAC", href: "/admin/security-audit/test-hmac", module: "dashboard" as AppModule, icon: Lock },
      { title: "Alertas de seguridad", href: "/admin/security-alerts", module: "dashboard" as AppModule, icon: Bell },
      { title: "Hallazgos seguridad", href: "/admin/security-findings", module: "dashboard" as AppModule, icon: Lock },
      { title: "Plantillas avisos", href: "/admin/notifications/templates", module: "dashboard" as AppModule, icon: FileText },
      { title: "Google Business", href: "/admin/gbp", module: "dashboard" as AppModule, icon: Globe },
      { title: "Search Console", href: "/admin/settings/gsc", module: "dashboard" as AppModule, icon: Globe },
      { title: "Gastos duplicados", href: "/admin/expense-duplicate-events", module: "dashboard" as AppModule, icon: List },
      { title: "Monitor 404", href: "/admin/error-404-monitor", module: "dashboard" as AppModule, icon: List },
      { title: "Rechazos UI", href: "/admin/ui-only-rejections", module: "dashboard" as AppModule, icon: List },
      { title: "Telemetría scroll", href: "/admin/touch-scroll-telemetry", module: "dashboard" as AppModule, icon: List },
      { title: "Telemetría combobox", href: "/admin/combobox-telemetry", module: "dashboard" as AppModule, icon: List },
    ],
  },
  {
    title: "Alertas de alquiler",
    icon: Bell,
    href: "/admin/alertas",
    module: "dashboard" as AppModule,
  },
  {
    title: "CMS",
    icon: PanelLeft,
    children: [
      { title: "Editor Landing", href: "/cms/landing", module: "dashboard" as AppModule, icon: Globe },
      { title: "Páginas", href: "/cms/pages", module: "dashboard" as AppModule, icon: FileText },
      { title: "Blog Dashboard", href: "/cms/blog", module: "dashboard" as AppModule, icon: LayoutDashboard },
      { title: "Blog Posts", href: "/cms/blog/posts", module: "dashboard" as AppModule, icon: FileText },
      { title: "Media Library", href: "/cms/blog/media", module: "dashboard" as AppModule, icon: FileStack },
      { title: "Categorías/Tags", href: "/cms/blog/categories", module: "dashboard" as AppModule, icon: Tags },
      { title: "Comentarios", href: "/cms/blog/comments", module: "dashboard" as AppModule, icon: MessageSquare },
    ],
  },
  {
    title: "Planes SaaS",
    icon: Sparkles,
    href: "/admin/saas",
    module: "dashboard" as AppModule,
  },
  {
    title: "Suscripciones",
    icon: CreditCard,
    href: "/admin/suscripciones",
    module: "dashboard" as AppModule,
  },
  {
    title: "Duplicar Datos ERP",
    icon: Copy,
    href: "/admin/data-duplication",
    module: "dashboard" as AppModule,
  },
  {
    title: "Configuración FTP",
    icon: Server,
    href: "/settings/ftp",
    module: "dashboard" as AppModule,
  },
  {
    title: "Admin Google Business",
    icon: Shield,
    href: "/admin/gbp",
    module: "dashboard" as AppModule,
  },
  {
    title: "SEO CRO UX",
    icon: TrendingUp,
    module: "seo_cro_ux" as AppModule,
    children: [
      { title: "Gestión SEO Avanzada", href: "/admin/seo-management", module: "seo_cro_ux" as AppModule, icon: Settings2 },
      { title: "Rendimiento Keywords", href: "/admin/seo-keywords", module: "seo_cro_ux" as AppModule, icon: TrendingUp },
      { title: "Rank Tracking", href: "/admin/seo-rank-tracking", module: "seo_cro_ux" as AppModule, icon: List },
      { title: "CRO & A/B Testing", href: "/admin/cro", module: "seo_cro_ux" as AppModule, icon: BarChart3 },
      { title: "SEO para IA", href: "/admin/seo-ia", module: "seo_cro_ux" as AppModule, icon: Sparkles },
      { title: "Sitemaps Dinámicos", href: "/admin/seo-sitemap", module: "seo_cro_ux" as AppModule, icon: MapIcon },
      { title: "G. Search Console", href: "/admin/settings/gsc", module: "seo_cro_ux" as AppModule, icon: Search },
    ],
  },
  {
    title: "Sistema",
    icon: Server,
    href: "/admin/system",
    module: "dashboard" as AppModule,
  },
  {
    title: "Importar Productos",
    icon: Download,
    href: "/erp/import-products",
    module: "dashboard" as AppModule,
  },
  {
    title: "Importar Patinetes",
    icon: Bike,
    href: "/erp/import-scooters",
    module: "dashboard" as AppModule,
  },
  {
    title: "Importar eWheel",
    icon: Download,
    href: "/erp/import-ewheel",
    module: "dashboard" as AppModule,
  },
  {
    title: "Integraciones",
    icon: Layers,
    children: [
      { title: "Marketplaces", href: "/integrations/marketplaces", module: "erp_orders" as AppModule, icon: Store },
      { title: "Config. Marketplaces", href: "/integrations/marketplaces/settings", module: "erp_orders" as AppModule, icon: Cog },
      { title: "Productos MP", href: "/integrations/marketplaces/products", module: "erp_orders" as AppModule, icon: Package },
      { title: "WooCommerce", href: "/integrations/woocommerce", module: "erp_orders" as AppModule, icon: ShoppingCart },
      { title: "Pedidos WC", href: "/integrations/woocommerce/orders", module: "erp_orders" as AppModule, icon: ClipboardList },
      { title: "Envíos", href: "/integrations/shipping", module: "erp_orders" as AppModule, icon: Truck },
      { title: "Config. Envíos", href: "/integrations/shipping/settings", module: "erp_orders" as AppModule, icon: Cog },
    ],
  },
  {
    title: "Google Business",
    icon: MapPin,
    children: [
      { title: "Dashboard", href: "/gbp", module: "dashboard" as AppModule, icon: LayoutDashboard },
      { title: "Perfiles", href: "/gbp/profiles", module: "dashboard" as AppModule, icon: Building2 },
      { title: "Publicaciones", href: "/gbp/posts", module: "dashboard" as AppModule, icon: FileText },
      { title: "Reseñas", href: "/gbp/reviews", module: "dashboard" as AppModule, icon: Star },
      { title: "Fotos", href: "/gbp/photos", module: "dashboard" as AppModule, icon: FileStack },
      { title: "Servicios", href: "/gbp/services", module: "dashboard" as AppModule, icon: Briefcase },
      { title: "Analíticas", href: "/gbp/analytics", module: "dashboard" as AppModule, icon: BarChart3 },
      { title: "Configuración", href: "/gbp/settings", module: "dashboard" as AppModule, icon: Settings },
    ],
  },
  {
    title: "Ads",
    icon: Megaphone,
    children: [
      { title: "Dashboard", href: "/ads", module: "dashboard" as AppModule, icon: LayoutDashboard },
      { title: "Presupuesto", href: "/ads/budget", module: "dashboard" as AppModule, icon: Wallet },
      { title: "Configuración", href: "/ads/settings", module: "dashboard" as AppModule, icon: Settings },
    ],
  },
  {
    title: "Matrículas",
    icon: FileText,
    children: [
      { title: "Dashboard", href: "/registrations", module: "dashboard" as AppModule, icon: Gauge },
      { title: "Solicitudes", href: "/registrations/list", module: "dashboard" as AppModule, icon: ClipboardList },
      { title: "Configuración", href: "/registrations/settings", module: "dashboard" as AppModule, icon: Settings },
    ],
  },
  {
    title: "Web Local",
    icon: Globe,
    children: [
      { title: "Analíticas", href: "/local-web/analytics", module: "dashboard" as AppModule, icon: BarChart3 },
      { title: "Administración", href: "/admin/local-websites", module: "dashboard" as AppModule, icon: Shield },
    ],
  },
  {
    title: "Tienda Online",
    icon: Store,
    children: [
      { title: "Pedidos", href: "/ecommerce/orders", module: "dashboard" as AppModule, icon: ClipboardList },
      { title: "Cupones", href: "/ecommerce/coupons", module: "dashboard" as AppModule, icon: Ticket },
    ],
  },
];

interface AppSidebarProps {
  collapsed: boolean;
  mobileOpen: boolean;
  onToggle: () => void;
  onMobileClose: () => void;
}

export function AppSidebar({ collapsed, mobileOpen, onToggle, onMobileClose }: AppSidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const { canAccess, loading, roles, isAdmin } = usePermissions();
  const { hasActiveSubscription, canAccessModule, loading: workspaceLoading, isOwner, currentPlan } = useWorkspace();
  const { isSuperAdmin, loading: superAdminLoading } = useSuperAdmin();
  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  const handleSignOut = async () => {
    await signOut();
    navigate("/auth");
  };

  // Find which group contains the current route (check both navItems and superAdminNavItems)
  const activeGroup = useMemo(() => {
    const allItems = [...navItems, ...superAdminNavItems];
    for (const item of allItems) {
      if (item.children) {
        const isChildActive = item.children.some((child) => location.pathname === child.href || location.pathname.startsWith(child.href + "/"));
        if (isChildActive) return item.title;
      }
    }
    return null;
  }, [location.pathname]);

  // Auto-expand the group containing the active route
  useEffect(() => {
    if (activeGroup && !expandedItems.includes(activeGroup)) {
      setExpandedItems((prev) => [...prev, activeGroup]);
    }
  }, [activeGroup]);

  // Close mobile menu when route changes
  useEffect(() => {
    onMobileClose();
  }, [location.pathname]);

  const toggleExpanded = (title: string) => {
    setExpandedItems((prev) =>
      prev.includes(title)
        ? prev.filter((item) => item !== title)
        : [...prev, title]
    );
  };

  const collapseAll = () => {
    setExpandedItems([]);
  };

  const isExpanded = (title: string) => expandedItems.includes(title);

  const isActive = (href?: string, children?: NavChild[]) => {
    if (href && location.pathname === href) return true;
    if (children) {
      return children.some((child) => location.pathname === child.href);
    }
    return false;
  };

  // Filter nav items based on permissions AND subscription
  const filterNavItems = (items: NavItem[]): NavItem[] => {
    // Wait until superadmin state is resolved to avoid rendering incomplete menus
    if (superAdminLoading) {
      return [];
    }

    // Super admin always has access to everything
    if (isSuperAdmin) {
      return items;
    }

    // If still loading (for non-super admins), return minimal items
    if (loading || workspaceLoading) {
      return items.filter(item => item.href === "/" || item.module === "dashboard");
    }
    
    // Workspace admin has access to all modules in their subscription plan
    // but we still filter by plan-specific modules below
    
    const isEnterprise = currentPlan === "enterprise";
    const helpAccess = getHelpAccess(currentPlan, hasActiveSubscription, isSuperAdmin);
    
    // Filter out enterprise-only sections for non-enterprise users
    const filtered = items.filter(item => {
      // Seguros and Formación/Ayuda are only for Enterprise or SuperAdmin
      if (isSuperAdmin) return true;
      if (item.title === "Formación" && !helpAccess.canView) return false;
      if (item.title === "Ayuda" && !isEnterprise) return false;
      if (item.title === "Seguros" && !isEnterprise) return false;
      
      // SEO CRO UX is for Superadmin only as requested
      if ((item.module === "seo_cro_ux" || item.title === "SEO CRO UX") && !isSuperAdmin) return false;
      
      return true;
    });
    
    return filtered
      .map((item) => {
        if (item.children) {
          const filteredChildren = item.children.filter((child) => {
            // Admin-only pages within modules
            if (child.href === "/admin/help") return helpAccess.canView;
            if (child.href === "/admin/formation" && !isSuperAdmin) return false;
            if (child.href === "/admin/local-websites" && !isSuperAdmin) return false;
            
            // SEO CRO UX is for Superadmin only as requested
            if ((child.module === "seo_cro_ux" || child.title.toLowerCase().includes("seo")) && !isSuperAdmin) return false;
            
            // Admins have access to everything within their plan
            if (isAdmin) return true;
            
            if (isOwner && hasActiveSubscription) {
              // Allow settings and subscription pages regardless
              if (child.module === "settings" || child.module === "dashboard") return true;
              // Check if module is in subscription plan
              return canAccessModule(child.module);
            }
            
            // For non-owners, check role permission first
            const hasRolePermission = canAccess(child.module, "view");
            if (!hasRolePermission) return false;
            
            // Allow settings and subscription pages regardless of subscription
            if (child.module === "settings" || child.module === "dashboard") return true;
            
            // Check if module is in subscription plan
            return hasActiveSubscription && canAccessModule(child.module);
          });
          if (filteredChildren.length === 0) return null;
          return { ...item, children: filteredChildren };
        }
        if (item.module) {
          // Admins have access to everything within their plan
          if (isAdmin) return item;
          
          // Workspace owner has access to all modules in their subscription plan
          if (isOwner && hasActiveSubscription) {
            if (item.module === "dashboard" || item.module === "settings") return item;
            return canAccessModule(item.module) ? item : null;
          }
          
          // For non-owners, check role permission
          const hasRolePermission = canAccess(item.module, "view");
          if (!hasRolePermission) return null;
          
          // Allow dashboard and settings regardless of subscription
          if (item.module === "dashboard" || item.module === "settings") return item;
          
          // Check subscription
          if (!hasActiveSubscription || !canAccessModule(item.module)) {
            return null;
          }
        }
        return item;
      })
      .filter(Boolean) as NavItem[];
  };

  // Get visible nav items and add super admin items if applicable
  const baseVisibleNavItems = filterNavItems(navItems);
  const visibleNavItems = superAdminLoading
    ? []
    : isSuperAdmin
      ? [...baseVisibleNavItems, ...superAdminNavItems]
      : baseVisibleNavItems;

  const getRoleBadge = () => {
    if (isAdmin) return { label: "Admin", class: "bg-primary/20 text-primary" };
    if (roles.includes("manager")) return { label: "Manager", class: "bg-success/20 text-success" };
    if (roles.includes("analyst")) return { label: "Analista", class: "bg-info/20 text-info" };
    if (roles.includes("operator")) return { label: "Operador", class: "bg-warning/20 text-warning" };
    return { label: "Usuario", class: "bg-muted text-muted-foreground" };
  };

  const roleBadge = getRoleBadge();

  const sidebarContent = (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="flex h-16 items-center justify-between border-b border-sidebar-border px-4">
        <div className="flex items-center">
          <DynamicLogo 
            width={32} 
            height={32} 
            type="dashboard"
            collapsed={collapsed}
            showText
            textClassName="text-base"
          />
        </div>
        {/* Desktop toggle */}
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggle}
          className="hidden md:flex h-8 w-8 text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
        >
          {collapsed ? <Menu className="h-4 w-4" /> : <X className="h-4 w-4" />}
        </Button>
        {/* Mobile close */}
        <Button
          variant="ghost"
          size="icon"
          onClick={onMobileClose}
          className="md:hidden h-8 w-8 text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Role Badge */}
      {!collapsed && (
        <div className="px-4 py-3 border-b border-sidebar-border">
          <div className="flex items-center gap-2">
            <Shield className="h-4 w-4 text-muted-foreground" />
            <span className={cn("text-xs font-medium px-2 py-0.5 rounded-full", roleBadge.class)}>
              {roleBadge.label}
            </span>
          </div>
        </div>
      )}

      {/* Workspace Selector (superadmin) */}
      {!collapsed && isSuperAdmin && (
        <div className="px-4 py-2 border-b border-sidebar-border">
          <WorkspaceSelector />
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-3">
        {/* Collapse all button */}
        {!collapsed && expandedItems.length > 0 && (
          <button
            onClick={collapseAll}
            className="flex items-center gap-2 w-full px-3 py-2 mb-2 text-xs text-muted-foreground hover:text-foreground hover:bg-sidebar-accent rounded-md transition-colors"
          >
            <ChevronsDownUp className="h-3.5 w-3.5" />
            <span>Colapsar menús</span>
          </button>
        )}
        {superAdminLoading || loading || workspaceLoading ? (
          <div className="space-y-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-10 w-full bg-sidebar-accent" />
            ))}
          </div>
        ) : (
          <ul className="space-y-1">
            {visibleNavItems.map((item) => (
              <li key={item.title}>
                {item.href ? (
                  <NavLink
                    to={item.href}
                    className={({ isActive }) =>
                      cn("sidebar-nav-item", isActive && "active")
                    }
                  >
                    {item.icon && <item.icon className="h-5 w-5 shrink-0" />}
                    {!collapsed && <span>{item.title}</span>}
                  </NavLink>
                ) : (
                  <>
                    <button
                      onClick={() => toggleExpanded(item.title)}
                      className={cn(
                        "sidebar-nav-item w-full cursor-pointer",
                        isActive(undefined, item.children) && "active"
                      )}
                    >
                      {item.icon && <item.icon className="h-5 w-5 shrink-0" />}
                      {!collapsed && (
                        <>
                          <span className="flex-1 text-left">{item.title}</span>
                          <ChevronRight 
                            className={cn(
                              "h-4 w-4 transition-transform duration-300 ease-out",
                              isExpanded(item.title) && "rotate-90"
                            )} 
                          />
                        </>
                      )}
                    </button>
                    {/* Show children when expanded */}
                    {!collapsed && item.children && (
                      <ul 
                        className={cn(
                          "ml-4 mt-1 space-y-1 border-l border-sidebar-border pl-4 overflow-hidden transition-all duration-300 ease-out",
                          isExpanded(item.title) 
                            ? "max-h-[1000px] opacity-100" 
                            : "max-h-0 opacity-0"
                        )}
                      >
                        {item.children.map((child, index) => (
                          <li 
                            key={child.href}
                            className={cn(
                              "transition-all duration-200",
                              isExpanded(item.title) 
                                ? "translate-x-0 opacity-100" 
                                : "-translate-x-2 opacity-0"
                            )}
                            style={{ 
                              transitionDelay: isExpanded(item.title) 
                                ? `${index * 50}ms` 
                                : "0ms" 
                            }}
                          >
                            <NavLink
                              to={child.href}
                              className={({ isActive }) =>
                                cn(
                                  "sidebar-nav-item text-sm",
                                  isActive && "active"
                                )
                              }
                            >
                              {child.icon && <child.icon className="h-4 w-4 shrink-0" />}
                              <span>{child.title}</span>
                            </NavLink>
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </nav>

      {/* Footer with Logout */}
      <div className="border-t border-sidebar-border p-3">
        {!collapsed && (
          <div className="text-xs text-muted-foreground mb-3">
            <p>Módulos accesibles: {visibleNavItems.length}</p>
          </div>
        )}
        <button
          onClick={handleSignOut}
          className={cn(
            "sidebar-nav-item w-full text-destructive hover:bg-destructive/10 hover:text-destructive",
            collapsed && "justify-center"
          )}
        >
          <LogOut className="h-5 w-5 shrink-0" />
          {!collapsed && <span>Cerrar sesión</span>}
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-background/80 backdrop-blur-sm md:hidden"
          onClick={onMobileClose}
        />
      )}

      {/* Mobile sidebar */}
      <aside
        key={`mobile-sidebar-${currentPlan}`}
        className={cn(
          "fixed left-0 top-0 z-50 h-screen w-full sm:w-72 border-r border-sidebar-border bg-sidebar transition-transform duration-300 md:hidden",
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        )}
        style={{ background: "var(--gradient-sidebar)" }}
      >
        {sidebarContent}
      </aside>

      {/* Desktop/Tablet sidebar */}
      <aside
        className={cn(
          "fixed left-0 top-0 z-40 h-screen border-r border-sidebar-border bg-sidebar transition-all duration-300 hidden md:block",
          collapsed ? "w-16" : "w-64"
        )}
        style={{ background: "var(--gradient-sidebar)" }}
      >
        {sidebarContent}
      </aside>
    </>
  );
}
